import React, { useMemo } from "react";
import { useTranslation } from "react-i18next"
import { colors, fonts, glyphs } from "../../../Styles"
import { CoreWidget } from "../..";
import { AttentionTypes, CreateAttentionStyles, ListStyle_Types } from "../../../Definitions";
import { NewButtonList } from "../../BaseComponents/Buttons/newButton";

interface systemControls_props {
  focus_view?: boolean;
  handleButtonClick: (i: number) => void;
  systemRunning: boolean
  attentionType: AttentionTypes;
  buttonContents: string[];
  buttonEnhancers: JSX.Element[];
}
/**
 * SystemControls
 * @returns A widget with btns to controle the system.
 */
export default function SystemControls({ focus_view, attentionType, handleButtonClick, systemRunning, buttonContents, buttonEnhancers }:systemControls_props) {
  const {t} = useTranslation(); 

  const { textColor, attentionColor, secondaryAttentionText } = useMemo(() => {
    var _Style = CreateAttentionStyles(t, attentionType)
    return _Style
  }, [t, attentionType]);

  const systemPower = useMemo(() => {
    return systemRunning ? {
      systemPower: attentionType === AttentionTypes.alert || attentionType === AttentionTypes.warning ? `${t("systemControls.general.runningWith")} ${secondaryAttentionText}` : t(`systemControls.states.${AttentionTypes.good}`, {defaultValue: "system running"}),
      statusColor: attentionColor,
      buttonText: t("systemControls.general.stopSystem", {defaultValue: "stop system"}),
      buttonEnhancer: glyphs.On({ size: focus_view ? "40px" : "35px" }),
      textColor: textColor
    } : {
      systemPower: t(`systemControls.states.${AttentionTypes.neutral}`, {defaultValue: "system off"}),
      statusColor: colors.systemRed,
      buttonText: t("systemControls.general.startSystem", { defaultValue: "start system" }),
      buttonEnhancer: glyphs.Off({ size: focus_view ? "40px" : "35px" }),
      textColor: textColor
    };
  }, [systemRunning, attentionType, t, secondaryAttentionText, attentionColor, focus_view, textColor]);

  const buttonListContents = useMemo(() => ([
    ...buttonContents,
    systemPower.buttonText
  ]), [t, systemPower.buttonText]);
  
  const buttonListStartEnhancers = useMemo(() => ([
    ...buttonEnhancers,
    systemPower.buttonEnhancer
  ]), [focus_view, systemPower.buttonEnhancer]);

  const buttonListDisabledState = useMemo(() => ([
    systemRunning ? false : true,
    systemRunning ? false : true,
    systemRunning ? false : true,
    false
  ]), [systemRunning]);
  
  return(
    <CoreWidget
      header={<p style={{...fonts.header, ...(focus_view ? fonts.fontSize2 : fonts.fontSize3), color: colors.greyLightTertiary}}>{t("systemControls.general.systemControls", {defaultValue: "System Controls"})}</p>}
      bgColor={colors.greyPrimary}
      width={"100%"}
      height={"fit-content"}
      content={
        <div style={{display: "flex", flexDirection: "column", gap: "20px", marginBottom: "20px"}}>
          <div style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "5px"}}>
            <p style={{...(focus_view ? fonts.fontSize3 : fonts.fontSize4), ...fonts.paragraph, color: colors.greyLightTertiary}}>{`${t("systemControls.general.systemState", {defaultValue: "System state"})}:`}</p>
            <div style={{...(focus_view ? fonts.fontSize3 : fonts.fontSize4), ...fonts.header, backgroundColor: systemPower.statusColor, color: systemPower.textColor, padding: "4px 5px", borderRadius: "6px", width: "fit-content"}}>{ systemPower.systemPower }</div>
          </div>
          <NewButtonList
            focus_view={focus_view}
            contents={buttonListContents}
            startEnhancers={buttonListStartEnhancers}
            overrides={{
              baseStyles: {backgroundColor: colors.greySecondary},
              hoverStyles: {backgroundColor: colors.greyDarkSecondary},
              disabledStyles: {opacity: "0.3"}
            }}
            listOverrides={{
              listStyle: ListStyle_Types.vertical,
              listGap: "10px"
            }}
            disabled={buttonListDisabledState}
            onClick={handleButtonClick}
          />
        </div>
      }
    />)
}

